/**
 * Small formatting helpers used across views.
 * Kept dependency-free so tree-shaking is trivial.
 */

/** Format a ngonka bigint string with thousands separators. */
export function formatNgonka(raw: string): string {
  // Negative values supported (overdraft).
  const isNeg = raw.startsWith('-');
  const digits = isNeg ? raw.slice(1) : raw;
  const withSep = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return (isNeg ? '-' : '') + withSep;
}

/** ngonka → GNK display (1 GNK = 1e9 ngonka). Sign-preserving. */
export function ngonkaToGnk(raw: string): string {
  const big = BigInt(raw);
  const neg = big < 0n;
  const abs = neg ? -big : big;
  // Compute on the absolute value so the sign is never lost when |value| < 1
  // GNK (whole part rounds to 0n, which has no '-' of its own).
  const whole = abs / 1_000_000_000n;
  const frac = abs % 1_000_000_000n;
  const fracStr = frac.toString().padStart(9, '0').replace(/0+$/, '');
  const body = fracStr.length === 0 ? `${whole}` : `${whole}.${fracStr}`;
  return neg ? `-${body}` : body;
}

export function formatUsd(value: number): string {
  if (!Number.isFinite(value)) return '—';
  return value.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 6 });
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

export function shortId(id: string, head = 8, tail = 4): string {
  if (id.length <= head + tail + 1) return id;
  return `${id.slice(0, head)}…${id.slice(-tail)}`;
}
